import { useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/auth';
import { FreeTierLayout } from './FreeTierLayout';
import { ProTierLayout } from './ProTierLayout';
import { EliteTierLayout } from './EliteTierLayout';

type Props = {
  onSubscriptionSuccess?: (sessionId: string | null) => void;
  onSubscriptionCanceled?: () => void;
  onCheckoutError?: () => void;
};

export const SubscriptionPage = ({ onSubscriptionSuccess, onSubscriptionCanceled, onCheckoutError }: Props) => {
  const { t } = useTranslation();
  const { sessionUser } = useAuth();
  const currentTier = sessionUser?.subscription.tier ?? 'free';

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('success') === 'true') {
      const sessionId = urlParams.get('session_id');
      onSubscriptionSuccess?.(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (urlParams.get('canceled') === 'true') {
      onSubscriptionCanceled?.();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [onSubscriptionSuccess, onSubscriptionCanceled]);

  const handleCheckoutError = useCallback(
    (error: Error) => {
      console.error('Checkout error:', error);
      onCheckoutError?.();
    },
    [onCheckoutError]
  );

  const handlePortalError = useCallback((error: Error) => {
    // No toast: the only expected portal error is the 409 from clicking
    // "Cancel plan" twice (e.g. two tabs racing). `useCancelPortalSession`
    // invalidates current-user on settle, so the UI self-heals on the next
    // render — the button disables and the "Cancels on {date}" label appears.
    console.error('Portal error:', error);
  }, []);

  return (
    <Box sx={{ maxWidth: 1000 }}>
      <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        {t('subscription.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('subscription.subtitle')}
      </Typography>

      {currentTier === 'free' && (
        <FreeTierLayout onCheckoutError={handleCheckoutError} onPortalError={handlePortalError} />
      )}
      {currentTier === 'pro' && (
        <ProTierLayout onPortalError={handlePortalError} onCheckoutError={handleCheckoutError} />
      )}
      {currentTier === 'elite' && <EliteTierLayout onPortalError={handlePortalError} />}
    </Box>
  );
};
